import { createFileRoute, Link } from "@tanstack/react-router";
import { LastUpdated } from "@/components/site/LastUpdated";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { workTranslationOf } from "@/lib/medical-schema";
import { faqPageJson } from "@/lib/faq-schema";
import { faqQuestionNodes } from "@/lib/section-faqs";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { trackWeightLossCtaClick } from "@/lib/analytics";
import { PageSection } from "@/components/site/PageSection";
import { TelehealthCta } from "@/components/site/TelehealthCta";
import { listPosts } from "@/lib/posts";

const TITLE = "Medical Weight Loss in Northern Virginia | Virginia Bariatric";
const DESCRIPTION =
  "Physician-supervised medical weight loss with Dr. Matthew Fitzer — GLP-1 medications like Wegovy and Zepbound, combination therapy, and long-term follow-up.";

const ARTICLE_SLUGS = [
  "weight-regain-after-stopping-injectable-medication-whats-the-reality",
  "weight-regain-after-wegovy-zepbound-treatment-part-1",
  "weight-regain-after-wegovy-zepbound-treatment-part-2",
  "weight-regain-after-wegovy-zepbound-treatment-part-3",
  "semaglutide-versus-weight-loss-surgery-outcomes",
];

const FAQS = [
  {
    q: "What is physician-supervised medical weight loss?",
    a: "Medical weight loss is a non-surgical, clinician-guided approach to obesity care. At Virginia Bariatric Surgery, Dr. Fitzer evaluates your health history, weight-related conditions, and goals, then recommends evidence-based treatments—often FDA-approved weight-loss medications such as Wegovy or Zepbound—combined with nutrition counseling and long-term follow-up.",
  },
  {
    q: "How do GLP-1 medications like Wegovy and Zepbound work?",
    a: "GLP-1 receptor agonists (semaglutide/Wegovy) and dual GIP/GLP-1 agonists (tirzepatide/Zepbound) mimic natural gut hormones that regulate appetite and satiety. They reduce hunger, slow stomach emptying, and help patients feel satisfied with smaller portions, leading to average weight losses of 15% to 21% while treatment continues.",
  },
  {
    q: "Who qualifies for medical weight loss treatment?",
    a: "Weight-loss medications are generally indicated for adults with a BMI of 30 or higher, or 27 or higher with a weight-related condition such as type 2 diabetes, high blood pressure, or sleep apnea. Dr. Fitzer reviews each patient's full medical history to determine candidacy safely. See our qualifications page for detailed BMI and comorbidity criteria.",
  },
  {
    q: "Will I regain weight if I stop taking Wegovy or Zepbound?",
    a: "Research and clinical experience show that weight regain is expected when injectable weight-loss medications are stopped without another intervention. Dr. Fitzer's blog series on Wegovy/Zepbound discontinuation explains why this happens and how ongoing treatment, combination therapy, or a surgical option can help maintain results.",
  },
  {
    q: "Are weight-loss medications covered by insurance?",
    a: "Coverage for GLP-1 and other anti-obesity medications varies widely by plan and changes frequently. Some commercial insurers cover them for eligible patients; others require prior authorization or exclude them entirely. Our Virginia coverage guide breaks down what Medicaid, COVA state employee plans, Sentara, UnitedHealthcare, Cigna, Anthem, Aetna, Medicare, TRICARE, and FEHB require for Zepbound, Wegovy, and Saxenda.",
  },
  {
    q: "How is medical weight loss different from bariatric surgery?",
    a: "Medical weight loss uses prescription medication, nutrition, and behavioral support rather than a procedure. It typically produces 15% to 21% total body weight loss and works only while treatment continues. Bariatric surgery such as sleeve gastrectomy or gastric bypass produces larger, more durable weight loss for appropriate candidates. Dr. Fitzer discusses both paths during consultation so patients can compare them honestly.",
  },
  {
    q: "Can I combine weight-loss medication with surgery?",
    a: "Yes. Medications can be used before surgery to reduce operative risk, after surgery to enhance or maintain weight loss, or as an alternative for patients who are not surgical candidates. The right sequence depends on your goals, health status, and response to treatment.",
  },
];

export const Route = createFileRoute("/medical-weight-loss/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: "https://virginiabariatric.com/medical-weight-loss" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "es_LA" },
        {
        property: "og:image",
        content:
          "https://virginiabariatric.com/__l5e/assets-v1/be659170-a675-48f4-89fc-223cac591050/semaglutide-model-unannotated.png",
      },
        {
        property: "og:image:alt",
        content: "Molecular model of semaglutide, a GLP-1 receptor agonist used for weight loss",
      },
        { name: "twitter:card", content: "summary_large_image" },
        {
        name: "twitter:image",
        content:
          "https://virginiabariatric.com/__l5e/assets-v1/be659170-a675-48f4-89fc-223cac591050/semaglutide-model-unannotated.png",
      },
        {
        name: "twitter:image:alt",
        content: "Molecular model of semaglutide, a GLP-1 receptor agonist used for weight loss",
      },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ] : []),
    ],
    links: [...(import.meta.env.SSR ? canonicalAndAlternates("/medical-weight-loss") : []),
        ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          // Every visible question on the page — the FAQ block plus the
          // question-style section headings — mapped to FAQPage.
          mainEntity: faqQuestionNodes("/medical-weight-loss", FAQS),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          lastReviewed: "2026-08-09",
          "@id": "https://virginiabariatric.com/medical-weight-loss#webpage",
          ...workTranslationOf("/medical-weight-loss"),
          url: "https://virginiabariatric.com/medical-weight-loss",
          name: "Medical Weight Loss in Northern Virginia",
          description: DESCRIPTION,
          inLanguage: "en-US",
          isPartOf: { "@id": "https://virginiabariatric.com/#website" },
          about: { "@type": "MedicalCondition", name: "Obesity" },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Patients considering medical weight loss",
          },
          specialty: "Bariatric",
          reviewedBy: PHYSICIAN_REF,
        }),
      },
    ] : [],
  }),
  component: MedicalWeightLossHub,
});

const CARDS = [
  {
    id: "medications",
    to: "/medical-weight-loss/medications",
    title: "Weight Loss Medications: Introduction",
    body: "Semaglutide (Wegovy), tirzepatide (Zepbound), and other FDA-approved anti-obesity medications, prescribed and monitored by Dr. Fitzer.",
    cta: "Learn About Weight Loss Medications: Introduction",
  },
  {
    id: "semaglutide",
    to: "/medical-weight-loss/semaglutide",
    title: "Semaglutide (Wegovy® & Ozempic®)",
    body: "Once-weekly injection or the new daily pill, with about 15% average weight loss and FDA-approved heart and liver benefits.",
    cta: "Learn About Semaglutide",
  },
  {
    id: "tirzepatide",
    to: "/medical-weight-loss/tirzepatide",
    title: "Tirzepatide (Zepbound® & Mounjaro®)",
    body: "The dual GIP/GLP-1 injection with the largest average weight loss of any medication — up to about 21%.",
    cta: "Learn About Tirzepatide",
  },
  {
    id: "oral-medications",
    to: "/medical-weight-loss/oral-medications",
    title: "Oral Medications",
    body: "Phentermine, Contrave®, orlistat, and diethylpropion — lower-cost options when a GLP-1 isn't right, covered, or tolerated, or when it needs a little help.",
    cta: "See Oral Options",
  },

  {
    id: "qualifications",
    to: "/medical-weight-loss/qualifications",
    title: "Qualifying for medication",
    body: "BMI thresholds, qualifying conditions, and the documentation Virginia plans require for weight-loss medication.",
    cta: "Check the Medication Requirements",
  },
  {
    id: "insurance",
    to: "/medical-weight-loss/insurance",
    title: "Insurance & Coverage",
    body: "What Anthem BCBS, Aetna, Medicare, and other major plans typically cover for weight-management medication.",
    cta: "Review Insurance Coverage",
  },
] as const;

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function MedicalWeightLossHub() {
  const articles = listPosts().filter((p) => ARTICLE_SLUGS.includes(p.slug));

  return (
    <>
      <PageSection className="pt-8 pb-6 text-center">
        <h1 className="text-4xl md:text-5xl font-serif">Medical Weight Loss in Northern Virginia</h1>
        <p className="mt-6 max-w-2xl mx-auto text-muted-foreground leading-[1.6]">
          Non-surgical, physician-supervised weight management for patients throughout Fairfax,
          Herndon, Reston, Arlington, Alexandria, Sterling, Ashburn, Chantilly, Manassas, and the
          greater DC metro area. Dr. Fitzer has more
          than two decades of experience prescribing{" "}
          <Link
            to="/medical-weight-loss/medications"
            className="text-brand-deep underline hover:no-underline"
          >
            weight loss medications
          </Link>{" "}
          — individually and in combination — alongside nutrition and long-term follow-up.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link

            to="/medical-weight-loss/qualifications"
            preload="intent"
            className="rounded-full bg-brand-logo px-6 py-2.5 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Do I qualify for medication?
          </Link>
          <Link
            to="/medical-weight-loss/insurance"
            preload="intent"
            className="rounded-full bg-brand-logo px-6 py-2.5 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Medication insurance coverage
          </Link>
        </div>
        <TelehealthCta />
      </PageSection>

      <PageSection
        fullWidth
        className="bg-brand-soft/50 py-8"
        innerClassName="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
      >
        {CARDS.map((c) => (
          <Link
            key={c.id}
            to={c.to}
            className="group block rounded-2xl border border-border bg-card py-4 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
          >
            <div className="h-9 w-9 rounded-full bg-brand/10 text-brand grid place-items-center font-serif text-lg">
              +
            </div>
            <h2 className="mt-3 text-lg font-serif text-brand-mid">{c.title}</h2>
            <p className="mt-3 text-sm text-muted-foreground leading-[1.6]">{c.body}</p>
            <span className="mt-2.5 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              {c.cta}
              <span aria-hidden="true"> →</span>
            </span>
          </Link>
        ))}
      </PageSection>

      {articles.length > 0 && (
        <PageSection fullWidth className="py-8" innerClassName="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-serif text-center">Related articles</h2>
          <p className="mt-3 text-center text-muted-foreground leading-[1.6]">
            Dr. Fitzer&rsquo;s analysis of medical weight-loss research, medication outcomes, and
            long-term considerations.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((post) => (
              <Link
                key={post.slug}
                to="/dr-fitzer-dissects-the-news/$slug"
                params={{ slug: post.slug }}
                className="group flex flex-col rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
              >
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {formatDate(post.date)}
                </span>
                <h3 className="mt-2 text-lg font-serif text-brand-mid leading-snug">
                  {post.title}
                </h3>
                <p className="mt-2 flex-grow text-sm text-muted-foreground leading-[1.6] line-clamp-3">
                  {post.excerpt.replace(/\[…\]/g, "").trim()}
                </p>
                <span className="mt-4 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
                  Read article
                  <span aria-hidden="true"> →</span>
                </span>
              </Link>
            ))}
          </div>
        </PageSection>
      )}

      <PageSection fullWidth className="py-8 bg-brand-soft/30" innerClassName="max-w-3xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-serif text-center">
          Medical weight loss FAQs
        </h2>
        <p className="mt-3 text-center text-muted-foreground leading-[1.6]">
          Answers to common questions about non-surgical weight management at Virginia Bariatric Surgery.
        </p>
        <div className="mt-6 space-y-4">
          {FAQS.map((f, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-border bg-card p-5"
            >
              <h3 className="text-lg font-serif text-brand-mid">{f.q}</h3>
              <p className="mt-2 text-foreground/80 leading-[1.6]">{f.a}</p>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection className="py-6 max-w-3xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-serif">
          When medication alone isn&rsquo;t enough
        </h2>
        <p className="mt-4 text-foreground/80 leading-[1.6]">
          Medications typically produce 15&ndash;21% total body weight loss and work only while
          treatment continues. Patients who need larger or more durable results often do better
          with a procedure. Dr. Fitzer reviews both paths in a single{" "}
          <Link to="/appointments" className="text-brand-deep underline hover:no-underline">
            consultation
          </Link>{" "}
          so you can compare them honestly. Learn more by visiting{" "}
          <Link
            to="/medical-weight-loss/medications"
            className="text-brand-deep underline hover:no-underline"
          >
            Weight Loss Medications: Introduction
          </Link>
          ,{" "}
          <Link to="/medical-weight-loss/qualifications" preload="intent" className="text-brand-deep underline hover:no-underline">
            qualifying for medication
          </Link>
          , and{" "}
          <Link to="/medical-weight-loss/insurance" preload="intent" className="text-brand-deep underline hover:no-underline">
            medication insurance coverage
          </Link>
          .
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/appointments"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Request a consultation
          </Link>
          <Link
            to="/services"
            onClick={() => trackWeightLossCtaClick("surgical", "/services", "medical_hub_footer_cta")}
            data-analytics-id="cta-bariatric-surgery"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Bariatric Surgery
          </Link>
        </div>
      <LastUpdated className="mt-10" />
      </PageSection>
    </>
  );
}
